import { booleanWithin, polygon as turfPolygon } from "@turf/turf";
import L from "leaflet";
import "leaflet-path-drag";
import { useEffect, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import { Polygon, useMap, useMapEvent } from "react-leaflet";

import DistanceLabels from "./DistanceLabels";

import { useLabelingContext } from "context/LabelingContextProvider";

import styles from "./map-point-builder.module.scss";

const MapPointBuilder = () => {
  const {
    labelingPolygon,
    updateCornerPosition,
    updateLabelPolygonPosition,
    cropPolygon,
    cropTransform
  } = useLabelingContext();
  const map = useMap();
  const polygonRef = useRef(null);

  const leafletMarkersRef = useRef<L.Marker[]>([]);

  const [rerenderCornerMarkersCounter, setRerenderCornerMarkersCounter] =
    useState(0);

  useEffect(() => {
    if (!map || !labelingPolygon) return;

    if (leafletMarkersRef.current.length === labelingPolygon.length) return;

    leafletMarkersRef.current.forEach((marker) => marker.remove());
    leafletMarkersRef.current = [];

    labelingPolygon.slice(0, 4).forEach((point, idx) => {
      const marker = L.marker(point, {
        icon: L.divIcon({
          html: renderToString(<div className={styles["marker"]} />),
          iconSize: [16, 16],
          className: "marker-reset"
        }),
        draggable: true,
        zIndexOffset: 1000
      }).addTo(map);

      marker.on("drag", (event) => {
        const { lat, lng } = event.target.getLatLng();

        updateCornerPosition(idx, [lat, lng]);
      });
      marker.on("dragend", () => {
        setRerenderCornerMarkersCounter(rerenderCornerMarkersCounter + 1);
      });

      leafletMarkersRef.current.push(marker);
    });

    return () => {
      leafletMarkersRef.current.forEach((marker) => marker.remove());
      leafletMarkersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, labelingPolygon.length, rerenderCornerMarkersCounter]);

  useEffect(() => {
    if (labelingPolygon?.length > 0)
      labelingPolygon.forEach((point, idx) => {
        if (leafletMarkersRef.current[idx]) {
          leafletMarkersRef.current[idx].setLatLng(point);
        }
      });
  }, [labelingPolygon]);

  useEffect(() => {
    return () => {
      leafletMarkersRef.current.forEach((marker) => marker.remove());
      leafletMarkersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!polygonRef.current || !map.pm || !cropPolygon) return;

    const polygon = polygonRef.current;

    let previousValidCoords = polygon.getLatLngs()[0] as L.LatLng[];

    const isPolygonInsideCrop = (dragCoords: L.LatLng[]) => {
      const labelCoords = dragCoords.map((p) => [p.lng, p.lat]) as [
        number,
        number
      ][];
      const cropCoords = cropTransform.geoCorners.map((corner) => [
        corner[1],
        corner[0]
      ]);

      const labelPoly = turfPolygon([[...labelCoords, labelCoords[0]]]);
      const cropPoly = turfPolygon([[...cropCoords, cropCoords[0]]]);

      return booleanWithin(labelPoly, cropPoly);
    };

    const handleDrag = () => {
      const coords = polygon.getLatLngs()[0] as L.LatLng[];

      if (isPolygonInsideCrop(coords)) {
        previousValidCoords = coords.map((p) => L.latLng(p));
      } else {
        polygon.setLatLngs([previousValidCoords]);
      }
    };

    const handleDragEnd = () => {
      const newCoords = polygon.getLatLngs()[0] as L.LatLng[];

      updateLabelPolygonPosition(newCoords);
    };

    polygon.pm.enableLayerDrag();

    polygon.on("pm:drag", handleDrag);
    polygon.on("pm:dragend", handleDragEnd);

    return () => {
      polygon.off("pm:drag", handleDrag);
      polygon.off("pm:dragend", handleDragEnd);
    };
  }, [
    cropPolygon,
    cropTransform?.geoCorners,
    map.pm,
    updateLabelPolygonPosition
  ]);

  useMapEvent("zoomend", () => {
    setRerenderCornerMarkersCounter((prev) => prev + 1);
  });

  return (
    <div className={styles["polygon-builder"]}>
      {labelingPolygon?.length > 1 && (
        <Polygon
          positions={labelingPolygon}
          color={"red"}
          fillColor={"red"}
          opacity={0.7}
          fillOpacity={0.5}
          ref={polygonRef}
        />
      )}
      {cropPolygon?.toString()}
      {cropPolygon && (
        <Polygon
          positions={cropPolygon}
          color="blue"
          fillColor="blue"
          opacity={0.5}
          fillOpacity={0.1}
        />
      )}
      {labelingPolygon?.length > 1 && (
        <DistanceLabels points={labelingPolygon.slice(0, -1)} />
      )}
    </div>
  );
};

export default MapPointBuilder;
